import { apiSlice } from "../../../features/api/apiSlice";

/**
 * Stores: the secretariat's item register and the receipts/issues that move it.
 *
 * Balances are never posted directly — recording a movement is what changes an
 * item's quantity, so every mutation here invalidates both lists.
 */

/** Laravel paginator envelope → the rows. */
const rows = (res: any): any[] =>
  Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];

/** Laravel paginator envelope → the meta a table needs. */
const meta = (res: any) => ({
  total: res?.total ?? res?.data?.total ?? 0,
  per_page: res?.per_page ?? res?.data?.per_page ?? 0,
  current_page: res?.current_page ?? res?.data?.current_page ?? 1,
  last_page: res?.last_page ?? res?.data?.last_page ?? 1,
});

export type StorePaged = { rows: any[]; meta: ReturnType<typeof meta> };

export type StoreItem = {
  id: number;
  name: string;
  code: string;
  category?: string | null;
  unit: string;
  description?: string | null;
  quantity: number;
  reorder_level: number;
  unit_cost?: number | null;
  location?: string | null;
  active: boolean;
  low_stock: boolean;
  stock_value: number;
};

export type MovementType = "in" | "out" | "adjustment";

const qs = (params: Record<string, any>) => {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  });
  const s = p.toString();
  return s ? `?${s}` : "";
};

/** Every write moves stock, so items, the register and the summary all refresh. */
/**
 * What a movement invalidates.
 *
 * Any movement can change a store's balance, an admin's holding, or both — a
 * transfer moves two stores, an issue moves a store and a person — so rather
 * than working out which, every movement refreshes all four lists.
 */
const STORE_TAGS = [
  { type: "StoreItems" as const, id: "LIST" },
  { type: "StoreMovements" as const, id: "LIST" },
  { type: "StoreSummary" as const, id: "LIST" },
  { type: "Stores" as const, id: "LIST" },
  { type: "StoreHoldings" as const, id: "LIST" },
  { type: "StoreReturns" as const, id: "LIST" },
];

export const storesApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /* ── Items ─────────────────────────────────────────────────────────── */

    storeGetItems: builder.query<
      StorePaged,
      { search?: string; category?: string; status?: string; low_stock?: boolean; per_page?: number } | void
    >({
      query: (arg) => `/store/items${qs({ per_page: 200, ...(arg ?? {}) })}`,
      transformResponse: (res: any) => ({ rows: rows(res), meta: meta(res) }),
      providesTags: [{ type: "StoreItems" as const, id: "LIST" }],
    }),

    /** One item plus its stock card (the API returns the last 100 movements). */
    storeGetItem: builder.query<any, string | number>({
      query: (id) => `/store/items/${id}`,
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: (_r, _e, id) => [{ type: "StoreItemDetails" as const, id: String(id) }],
    }),

    storeGetCategories: builder.query<string[], void>({
      query: () => "/store/items/categories",
      transformResponse: (res: any) => (Array.isArray(res) ? res : res?.data ?? []),
      providesTags: [{ type: "StoreItems" as const, id: "CATEGORIES" }],
    }),

    storeCreateItem: builder.mutation<any, any>({
      query: (body) => ({ url: "/store/items", method: "POST", body }),
      invalidatesTags: [...STORE_TAGS, { type: "StoreItems" as const, id: "CATEGORIES" }],
    }),

    // POST rather than PUT — some shared hosts block the other verbs (the API accepts both).
    storeUpdateItem: builder.mutation<any, { id: string | number; body: any }>({
      query: ({ id, body }) => ({ url: `/store/items/${id}`, method: "POST", body }),
      invalidatesTags: (_r, _e, arg) => [
        ...STORE_TAGS,
        { type: "StoreItems" as const, id: "CATEGORIES" },
        { type: "StoreItemDetails" as const, id: String(arg.id) },
      ],
    }),

    /** Deletes only an item that was never moved; otherwise the API deactivates it. */
    storeDeleteItem: builder.mutation<any, string | number>({
      query: (id) => ({ url: `/store/items/${id}`, method: "DELETE" }),
      invalidatesTags: STORE_TAGS,
    }),

    /* ── Movements ─────────────────────────────────────────────────────── */

    storeGetMovements: builder.query<
      StorePaged,
      {
        store_item_id?: string | number;
        /** One store's own card. */
        store_id?: string | number;
        /** "store" = what the stores did; "admin" = what is happening to stock out with people. */
        holder?: "store" | "admin" | "";
        admin_id?: string;
        type?: MovementType | "";
        from?: string;
        to?: string;
        search?: string;
        per_page?: number;
      } | void
    >({
      query: (arg) => `/store/movements${qs({ per_page: 200, ...(arg ?? {}) })}`,
      transformResponse: (res: any) => ({ rows: rows(res), meta: meta(res) }),
      providesTags: [{ type: "StoreMovements" as const, id: "LIST" }],
    }),

    /* ── The stores themselves ─────────────────────────────────────────── */

    storeGetStores: builder.query<any[], { active_only?: boolean } | void>({
      query: (arg) => `/store/stores${qs({ ...(arg ?? {}) })}`,
      transformResponse: (res: any) => (Array.isArray(res?.data) ? res.data : []),
      providesTags: [{ type: "Stores" as const, id: "LIST" }],
    }),

    storeGetStore: builder.query<any, string | number>({
      query: (id) => `/store/stores/${id}`,
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: (_r, _e, id) => [{ type: "Stores" as const, id: String(id) }],
    }),

    storeCreateStore: builder.mutation<any, any>({
      query: (body) => ({ url: "/store/stores", method: "POST", body }),
      invalidatesTags: [{ type: "Stores" as const, id: "LIST" }],
    }),

    storeUpdateStore: builder.mutation<any, { id: string | number; body: any }>({
      // POST rather than PUT — the host blocks the other verbs on some routes.
      query: ({ id, body }) => ({ url: `/store/stores/${id}`, method: "POST", body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: "Stores" as const, id: "LIST" },
        { type: "Stores" as const, id: String(arg.id) },
      ],
    }),

    storeDeleteStore: builder.mutation<any, string | number>({
      query: (id) => ({ url: `/store/stores/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "Stores" as const, id: "LIST" }],
    }),

    /** What admins are holding, having been issued it out of a store. */
    storeGetHoldings: builder.query<any[], { admin_id?: string; store_item_id?: string | number } | void>({
      query: (arg) => `/store/movements/holdings${qs({ ...(arg ?? {}) })}`,
      transformResponse: (res: any) => (Array.isArray(res?.data) ? res.data : []),
      providesTags: [{ type: "StoreHoldings" as const, id: "LIST" }],
    }),

    /**
     * Returns waiting on a store to confirm them.
     *
     * Nothing has moved while one of these is pending — the holder still holds
     * the stock — so this list is the store's inbox, not a history.
     */
    storeGetReturnRequests: builder.query<any[], { status?: string; store_id?: string | number } | void>({
      query: (arg) => `/store/movements/returns${qs({ ...(arg ?? {}) })}`,
      transformResponse: (res: any) => (Array.isArray(res?.data) ? res.data : []),
      providesTags: [{ type: "StoreReturns" as const, id: "LIST" }],
    }),

    storeConfirmReturn: builder.mutation<any, { id: string; decision_note?: string }>({
      query: ({ id, ...body }) => ({ url: `/store/movements/returns/${id}/confirm`, method: "POST", body }),
      invalidatesTags: STORE_TAGS,
    }),

    storeRejectReturn: builder.mutation<any, { id: string; decision_note?: string }>({
      query: ({ id, ...body }) => ({ url: `/store/movements/returns/${id}/reject`, method: "POST", body }),
      invalidatesTags: STORE_TAGS,
    }),

    /** Store → store. Two ledger rows, one move. */
    storeTransfer: builder.mutation<
      any,
      {
        store_item_id: string | number;
        from_store_id: string | number;
        to_store_id: string | number;
        quantity: number;
        movement_date?: string;
        reference?: string;
        note?: string;
      }
    >({
      query: (body) => ({ url: "/store/movements/transfer", method: "POST", body }),
      invalidatesTags: STORE_TAGS,
    }),

    /** Store → admin. The stock leaves the store into their hands. */
    storeIssueToAdmin: builder.mutation<
      any,
      {
        store_item_id: string | number;
        store_id: string | number;
        admin_id: string;
        quantity: number;
        movement_date?: string;
        reference?: string;
        note?: string;
      }
    >({
      query: (body) => ({ url: "/store/movements/issue", method: "POST", body }),
      invalidatesTags: STORE_TAGS,
    }),

    /** An admin debits what they hold, against money. */
    storeRecordSale: builder.mutation<
      any,
      {
        store_item_id: string | number;
        admin_id: string;
        quantity: number;
        unit_price?: number | null;
        amount?: number | null;
        sold_to?: string;
        movement_date?: string;
        reference?: string;
        note?: string;
      }
    >({
      query: (body) => ({ url: "/store/movements/sale", method: "POST", body }),
      invalidatesTags: STORE_TAGS,
    }),

    /** An admin gives stock back to a store. */
    storeReturnToStore: builder.mutation<
      any,
      {
        store_item_id: string | number;
        admin_id: string;
        store_id: string | number;
        quantity: number;
        movement_date?: string;
        reference?: string;
        note?: string;
      }
    >({
      query: (body) => ({ url: "/store/movements/return", method: "POST", body }),
      invalidatesTags: STORE_TAGS,
    }),

    /** Receipt, issue or stock-count correction — the only thing that moves a balance. */
    storeCreateMovement: builder.mutation<
      any,
      {
        store_item_id: string | number;
        /** Which store receives it, issues it, or is being corrected. */
        store_id: string | number;
        type: MovementType;
        quantity: number;
        unit_cost?: number | null;
        movement_date?: string;
        reference?: string;
        supplier?: string;
        issued_to_admin_id?: string | null;
        issued_to?: string;
        department_id?: number | string | null;
        note?: string;
      }
    >({
      query: (body) => ({ url: "/store/movements", method: "POST", body }),
      invalidatesTags: STORE_TAGS,
    }),

    /* ── The manager's front page ──────────────────────────────────────── */

    storeGetDashboard: builder.query<any, { store_id?: string | number } | void>({
      query: (arg) => `/store/dashboard${qs({ ...(arg ?? {}) })}`,
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: [{ type: "StoreSummary" as const, id: "DASHBOARD" }],
    }),

    /* ── Asking for stock, and suggesting items ────────────────────────── */

    storeGetRequests: builder.query<any[], { status?: string } | void>({
      query: (arg) => `/store/requests${qs({ ...(arg ?? {}) })}`,
      transformResponse: (res: any) => (Array.isArray(res?.data) ? res.data : []),
      providesTags: [{ type: "StoreRequests" as const, id: "LIST" }],
    }),

    storeCreateRequest: builder.mutation<
      any,
      { store_id: string | number; store_item_id: string | number; quantity: number; note?: string }
    >({
      query: (body) => ({ url: "/store/requests", method: "POST", body }),
      invalidatesTags: [{ type: "StoreRequests" as const, id: "LIST" }],
    }),

    /** Approving IS the transfer, so this refreshes the balances too. */
    storeApproveRequest: builder.mutation<any, { id: string; quantity?: number; decision_note?: string }>({
      query: ({ id, ...body }) => ({ url: `/store/requests/${id}/approve`, method: "POST", body }),
      invalidatesTags: [...STORE_TAGS, { type: "StoreRequests" as const, id: "LIST" }],
    }),

    storeRejectRequest: builder.mutation<any, { id: string; decision_note?: string }>({
      query: ({ id, ...body }) => ({ url: `/store/requests/${id}/reject`, method: "POST", body }),
      invalidatesTags: [{ type: "StoreRequests" as const, id: "LIST" }],
    }),

    storeCancelRequest: builder.mutation<any, string>({
      query: (id) => ({ url: `/store/requests/${id}/cancel`, method: "POST" }),
      invalidatesTags: [{ type: "StoreRequests" as const, id: "LIST" }],
    }),

    storeGetSuggestions: builder.query<any[], { status?: string } | void>({
      query: (arg) => `/store/suggestions${qs({ ...(arg ?? {}) })}`,
      transformResponse: (res: any) => (Array.isArray(res?.data) ? res.data : []),
      providesTags: [{ type: "StoreRequests" as const, id: "SUGGESTIONS" }],
    }),

    storeCreateSuggestion: builder.mutation<
      any,
      {
        store_id?: string | number;
        name: string;
        category?: string;
        unit?: string;
        suggested_price?: number;
        reason?: string;
      }
    >({
      query: (body) => ({ url: "/store/suggestions", method: "POST", body }),
      invalidatesTags: [{ type: "StoreRequests" as const, id: "SUGGESTIONS" }],
    }),

    /** Accepting creates the item, so the item register refreshes as well. */
    storeAcceptSuggestion: builder.mutation<
      any,
      { id: string; code: string; unit_cost?: number; reorder_level?: number; decision_note?: string }
    >({
      query: ({ id, ...body }) => ({ url: `/store/suggestions/${id}/accept`, method: "POST", body }),
      invalidatesTags: [
        { type: "StoreRequests" as const, id: "SUGGESTIONS" },
        { type: "StoreItems" as const, id: "LIST" },
      ],
    }),

    storeRejectSuggestion: builder.mutation<any, { id: string; decision_note?: string }>({
      query: ({ id, ...body }) => ({ url: `/store/suggestions/${id}/reject`, method: "POST", body }),
      invalidatesTags: [{ type: "StoreRequests" as const, id: "SUGGESTIONS" }],
    }),

    storeGetReports: builder.query<any, { from?: string; to?: string } | void>({
      query: (arg) => `/store/reports${qs({ ...(arg ?? {}) })}`,
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: [{ type: "StoreSummary" as const, id: "REPORTS" }],
    }),

    storeGetSummary: builder.query<any, { from?: string; to?: string } | void>({
      query: (arg) => `/store/movements/summary${qs({ ...(arg ?? {}) })}`,
      transformResponse: (res: any) => res?.data ?? res,
      providesTags: [{ type: "StoreSummary" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useStoreGetItemsQuery,
  useStoreGetItemQuery,
  useStoreGetCategoriesQuery,
  useStoreCreateItemMutation,
  useStoreUpdateItemMutation,
  useStoreDeleteItemMutation,
  useStoreGetMovementsQuery,
  useStoreCreateMovementMutation,
  useStoreGetSummaryQuery,
  useStoreGetDashboardQuery,
  useStoreGetReportsQuery,
  useStoreGetRequestsQuery,
  useStoreCreateRequestMutation,
  useStoreApproveRequestMutation,
  useStoreRejectRequestMutation,
  useStoreCancelRequestMutation,
  useStoreGetSuggestionsQuery,
  useStoreCreateSuggestionMutation,
  useStoreAcceptSuggestionMutation,
  useStoreRejectSuggestionMutation,
  useStoreGetStoresQuery,
  useStoreGetStoreQuery,
  useStoreCreateStoreMutation,
  useStoreUpdateStoreMutation,
  useStoreDeleteStoreMutation,
  useStoreGetHoldingsQuery,
  useStoreGetReturnRequestsQuery,
  useStoreConfirmReturnMutation,
  useStoreRejectReturnMutation,
  useStoreTransferMutation,
  useStoreIssueToAdminMutation,
  useStoreRecordSaleMutation,
  useStoreReturnToStoreMutation,
} = storesApi;
